import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { FaClipboardList, FaQuestionCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  ADD_MANUAL_TIP,
  ADD_MANUAL_TIPS,
  GET_TIP_QUEUE,
  REORDER_TIP_QUEUE,
  SEARCH_CB_TIPPERS,
  SET_TIP_QUEUE_STATUS,
} from "../../../queries/tipQueue";
import {
  TipQueue as TipQueueData,
  TipQueueEntry,
} from "../../../types/TipQueue";
import { EntryCard, Handlers, Lane } from "../../tipQueue/EntryCard";
import LoadingPage from "../../LoadingPage";
import { parseManualTips } from "../../../util/parseManualTips";

// Mods and the broadcaster work the tip queue here; the OBS widget mirrors
// it. Only tips that buy a live menu item are queued, plus tips added here.
const POLL_MS = 3000;
const OTHER = "__other__";

const Section = ({
  title,
  lane,
  entries,
  empty,
  handlers,
}: {
  title: string;
  lane: Lane;
  entries: TipQueueEntry[];
  empty: string;
  handlers: Handlers;
}) => (
  <section className="flex flex-col gap-2">
    <h3 className="font-semibold">
      {title} <span className="text-base-content/60">({entries.length})</span>
    </h3>
    {entries.length === 0 ? (
      <div className="text-sm text-base-content/60">{empty}</div>
    ) : (
      <ul className="flex flex-col gap-2">
        {entries.map((entry, index) => (
          <EntryCard
            key={entry.id}
            entry={entry}
            lane={lane}
            index={index}
            count={entries.length}
            handlers={handlers}
          />
        ))}
      </ul>
    )}
  </section>
);

const TipQueue = () => {
  const [showHelp, setShowHelp] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Add-by-hand form.
  const [username, setUsername] = useState("");
  const [pick, setPick] = useState<string>("");
  const [otherTokens, setOtherTokens] = useState("");
  const [otherName, setOtherName] = useState("");
  const [note, setNote] = useState("");
  // Straight to Banked - stays on between adds, for working down a list.
  const [bank, setBank] = useState(false);
  // Several at once from a pasted list.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  // The rows sent with the last bulk save, so refused ones can be kept.
  const [sentRows, setSentRows] = useState<{ line: number; raw: string }[]>([]);
  // Suggestions for the username as they type; any name is still allowed.
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(username.trim()), 250);
    return () => clearTimeout(t);
  }, [username]);
  const { data: searchData } = useQuery(SEARCH_CB_TIPPERS, {
    variables: { query: searchTerm },
    skip: searchTerm.length < 2,
  });
  const suggestions: string[] = searchData?.searchCbTippers ?? [];
  // Drawn by the page, not the browser's datalist, which some browsers
  // don't show at all.
  const [nameFocused, setNameFocused] = useState(false);
  const showSuggestions =
    nameFocused &&
    searchTerm.length >= 2 &&
    suggestions.length > 0 &&
    !(suggestions.length === 1 && suggestions[0] === username.trim());

  const { data, loading, error, refetch, startPolling, stopPolling } =
    useQuery(GET_TIP_QUEUE, { fetchPolicy: "cache-and-network" });
  useEffect(() => {
    startPolling(POLL_MS);
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const fail = (e: { message: string }) => toast.error(e.message);
  const [setStatus] = useMutation(SET_TIP_QUEUE_STATUS, { onError: fail });
  const [reorder] = useMutation(REORDER_TIP_QUEUE, { onError: fail });
  const [addTips, { loading: addingMany }] = useMutation(ADD_MANUAL_TIPS, {
    onError: fail,
    onCompleted: ({ addManualTips }) => {
      const { added, errors } = addManualTips;
      if (added.length) {
        toast.success(`Saved ${added.length} tip${added.length === 1 ? "" : "s"} for later`);
      }
      if (errors.length) {
        toast.error(`${errors.length} line${errors.length === 1 ? "" : "s"} refused - see the preview`);
        // Keep only what wasn't added, so saving again can't add twice.
        const refused = new Set(errors.map((e: { index: number }) => sentRows[e.index]?.line));
        setBulkText(
          parseManualTips(bulkText, menuItems)
            .filter((r) => r.error || refused.has(r.line))
            .map((r) => r.raw)
            .join("\n"),
        );
      } else {
        setBulkText("");
        setBulkOpen(false);
      }
      refetch();
    },
  });
  const [addTip, { loading: adding }] = useMutation(ADD_MANUAL_TIP, {
    onError: fail,
    onCompleted: ({ addManualTip }) => {
      toast.success(
        addManualTip?.status === "banked" ? "Saved for later" : "Added to the queue",
      );
      setUsername("");
      setNote("");
      setOtherTokens("");
      setOtherName("");
      refetch();
    },
  });

  const queue: TipQueueData | undefined = data?.getTipQueue;
  const menuItems = queue?.menuItems ?? [];

  const handlers: Handlers = {
    busyId,
    onStatus: async (entry, status) => {
      setBusyId(entry.id);
      try {
        const { data } = await setStatus({ variables: { id: entry.id, status } });
        if (data) {
          await refetch();
        }
      } finally {
        setBusyId(null);
      }
    },
    onMove: async (lane, entry, direction) => {
      const ids = (queue?.[lane] ?? []).map((e) => e.id);
      const from = ids.indexOf(entry.id);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= ids.length) return;
      ids.splice(from, 1);
      ids.splice(to, 0, entry.id);
      setBusyId(entry.id);
      try {
        const { data } = await reorder({ variables: { lane, ids } });
        if (data) {
          await refetch();
        }
      } finally {
        setBusyId(null);
      }
    },
  };

  const submitAdd = () => {
    const picked = menuItems.find((m) => String(m.amount) === pick);
    const tokens = picked ? picked.amount : Number(otherTokens);
    if (!username.trim()) {
      toast.error("Who is the tip from?");
      return;
    }
    if (!picked && (!Number.isInteger(tokens) || tokens <= 0)) {
      toast.error("Enter the token amount");
      return;
    }
    addTip({
      variables: {
        input: {
          username: username.trim(),
          tokens,
          itemName: picked ? null : otherName.trim() || null,
          message: note.trim() || null,
          bank,
        },
      },
    });
  };

  const parsed = parseManualTips(bulkText, menuItems);
  const good = parsed.filter((r) => !r.error);
  const submitBulk = () => {
    if (!good.length) return;
    setSentRows(good.map((r) => ({ line: r.line, raw: r.raw })));
    addTips({
      variables: {
        inputs: good.map((r) => ({
          username: r.username,
          tokens: r.tokens,
          itemName: r.itemName,
          message: r.message || null,
          bank: true,
        })),
      },
    });
  };

  return (
    <div className="bg-base-200 min-h-full flex flex-col p-0 sm:p-4 gap-4">
      <div className="card bg-base-100 flex-1 flex flex-col">
        <div className="card-body flex flex-col flex-1 !p-3 sm:!p-4 gap-4">
          <div className="flex items-center gap-2">
            <FaClipboardList size={24} className="text-success shrink-0" />
            <h2 className="card-title text-xl sm:text-2xl m-0">Tip Queue</h2>
            <button
              className="btn btn-ghost btn-sm btn-circle"
              onClick={() => setShowHelp(true)}
              aria-label="How the queue works"
              title="How the queue works"
            >
              <FaQuestionCircle size={20} className="text-base-content/60" />
            </button>
            <button
              className="btn btn-primary btn-sm ml-auto"
              onClick={() => refetch()}
            >
              Refresh
            </button>
          </div>

          {/* Add a tip by hand */}
          <div className="rounded-box border border-base-300 p-3 flex flex-col gap-2">
            <div className="font-semibold text-sm">Add a tip</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="relative">
                <input
                  className="input input-bordered input-sm w-full"
                  placeholder="CB username (type to search)"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onFocus={() => setNameFocused(true)}
                  // After the click on a suggestion has landed.
                  onBlur={() => setTimeout(() => setNameFocused(false), 150)}
                  onKeyDown={(e) => e.key === "Escape" && setNameFocused(false)}
                  autoComplete="off"
                  aria-autocomplete="list"
                  aria-expanded={showSuggestions}
                />
                {showSuggestions && (
                  <ul
                    role="listbox"
                    className="absolute z-20 left-0 right-0 mt-1 menu menu-sm bg-base-100 border border-base-300 rounded-box shadow max-h-56 overflow-y-auto flex-nowrap"
                  >
                    {suggestions.map((name) => (
                      <li key={name} role="option" aria-selected={false}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setUsername(name);
                            setNameFocused(false);
                          }}
                        >
                          {name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <select
                className="select select-bordered select-sm"
                value={pick}
                onChange={(e) => setPick(e.target.value)}
                aria-label="Menu item"
              >
                <option value="">Pick a menu item</option>
                {menuItems.map((m) => (
                  <option key={m.amount} value={String(m.amount)}>
                    {m.name} ({m.amount})
                  </option>
                ))}
                <option value={OTHER}>Other amount</option>
              </select>
              <input
                className="input input-bordered input-sm"
                placeholder="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            {pick === OTHER && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  className="input input-bordered input-sm"
                  type="number"
                  min={1}
                  placeholder="Tokens"
                  value={otherTokens}
                  onChange={(e) => setOtherTokens(e.target.value)}
                />
                <input
                  className="input input-bordered input-sm"
                  placeholder="What it's for"
                  value={otherName}
                  onChange={(e) => setOtherName(e.target.value)}
                />
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="label cursor-pointer gap-2 p-0">
                <input
                  type="checkbox"
                  className="toggle toggle-accent toggle-sm"
                  checked={bank}
                  onChange={(e) => setBank(e.target.checked)}
                />
                <span className="label-text text-sm">
                  Save for later (straight to Banked)
                </span>
              </label>
              <div className="flex gap-2">
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setBulkOpen(!bulkOpen)}
                >
                  {bulkOpen ? "Hide list entry" : "Add several…"}
                </button>
                <button
                  className={`btn btn-sm ${bank ? "btn-accent" : "btn-primary"}`}
                  onClick={submitAdd}
                  disabled={adding || !username.trim() || !pick}
                >
                  {adding ? "Adding..." : bank ? "Save for later" : "Add to queue"}
                </button>
              </div>
            </div>

            {bulkOpen && (
              <div className="rounded-box bg-base-200 p-3 flex flex-col gap-2 mt-1">
                <div className="text-sm">
                  One tip per line: <code>username, item or tokens, note</code>.
                  Everything here goes straight to <b>Banked</b>.
                </div>
                <textarea
                  className="textarea textarea-bordered textarea-sm w-full font-mono"
                  rows={5}
                  placeholder={"ghost_tipper, Spanks, promised last show\nnew_fan, 250, birthday song"}
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                />
                {parsed.length > 0 && (
                  <ul className="text-sm flex flex-col gap-1 max-h-48 overflow-y-auto">
                    {parsed.map((r) => (
                      <li key={r.line} className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-xs text-base-content/50 w-6">{r.line}.</span>
                        {r.error ? (
                          <span className="text-error">
                            {r.error}: <span className="font-mono">{r.raw}</span>
                          </span>
                        ) : (
                          <>
                            <span className="font-semibold">{r.username}</span>
                            <span className="text-accent">
                              {r.itemName ??
                                menuItems.find((m) => m.amount === r.tokens)?.name ??
                                `${r.tokens} tokens`}
                              {r.itemName && (
                                <span className="text-base-content/60"> ({r.tokens} tokens)</span>
                              )}
                            </span>
                            {r.message && (
                              <span className="text-base-content/60">“{r.message}”</span>
                            )}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex justify-end">
                  <button
                    className="btn btn-accent btn-sm"
                    onClick={submitBulk}
                    disabled={addingMany || good.length === 0}
                  >
                    {addingMany
                      ? "Saving..."
                      : `Save ${good.length} for later${parsed.length > good.length ? ` (skip ${parsed.length - good.length})` : ""}`}
                  </button>
                </div>
              </div>
            )}
          </div>

          {loading && !queue ? (
            <div className="flex-1 flex items-center justify-center">
              <LoadingPage />
            </div>
          ) : error && !queue ? (
            <div className="text-center py-8">
              <div className="text-error text-xl font-bold mb-2">
                Couldn't load the queue
              </div>
              <div className="text-gray-400">{error.message}</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Section
                title="Waiting"
                lane="waiting"
                entries={queue?.waiting ?? []}
                empty="Nothing waiting - tips that buy a menu item show up here."
                handlers={handlers}
              />
              <Section
                title="Banked"
                lane="banked"
                entries={queue?.banked ?? []}
                empty="Nothing banked."
                handlers={handlers}
              />
            </div>
          )}

          {queue && (
            <section className="flex flex-col gap-2">
              <button
                className="btn btn-ghost btn-sm w-fit"
                onClick={() => setShowDone(!showDone)}
              >
                {showDone ? "Hide" : "Show"} done in the last hour (
                {queue.done.length})
              </button>
              {showDone && (
                <ul className="flex flex-col gap-2">
                  {queue.done.length === 0 ? (
                    <li className="text-sm text-base-content/60">
                      Nothing done in the last hour.
                    </li>
                  ) : (
                    queue.done.map((entry, index) => (
                      <EntryCard
                        key={entry.id}
                        entry={entry}
                        lane="done"
                        index={index}
                        count={queue.done.length}
                        handlers={handlers}
                      />
                    ))
                  )}
                </ul>
              )}
            </section>
          )}
        </div>
      </div>

      {showHelp && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">How the tip queue works</h3>
            <div className="py-4 text-sm flex flex-col gap-2">
              <p>
                Every Chaturbate tip that buys something on the live tip menu
                lands in <b>Waiting</b>, newest first. Tips that don't match a
                menu item aren't queued. Waiting tips drop off after a day.
              </p>
              <p>
                <b>Bank</b> saves a tip for another time. Banked tips stay until
                someone marks them done.
              </p>
              <p>
                <b>Done</b> clears a tip. It stays under "done in the last
                hour" so a mis-click can be restored.
              </p>
              <p>
                Use ▲ ▼ to order a list. The OBS widget shows the same lists in
                the same order, but can't change them.
              </p>
              <p>
                <b>Add a tip</b> puts something in the queue by hand. It's
                marked with who added it and never counts in tip statistics.
              </p>
            </div>
            <div className="modal-action">
              <button className="btn" onClick={() => setShowHelp(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TipQueue;
